import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { render } from '@markbird/markdown';
import type { GetShareResponse, UpdateShareRequest } from '@markbird/shared';
import { renderMermaid } from '../lib/mermaid';
import { renderKatex } from '../lib/katex';
import { resolveImagesInDom } from '../lib/images';
import { createDocument, setLastOpenDocId } from '../lib/library';
import { useTheme } from '../hooks/useTheme';
import { useT } from '../i18n/useT';
import { CollabEditor } from '../components/CollabEditor';
import { collabEnabled, makeCollabUser, type CollabUser } from '../lib/collab';
import { Icon } from '../components/Icon';
import { ReportDialog } from '../components/ReportDialog';
import { forgetLocalShare } from '../lib/localShares';
import { SitePage } from './SitePage';

type State =
  | { status: 'loading' }
  /** The slug belongs to a published site, not to a single note. */
  | { status: 'site' }
  | { status: 'password'; error?: string }
  | { status: 'error'; message: string }
  | { status: 'ready'; doc: GetShareResponse };

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Share viewer for `/s/:token`. Fetches the snapshot from the same-origin API
 * and renders it through the SAME sanitizing pipeline the editor uses — so
 * shared content can't inject scripts. Read-only shares show a rendered
 * article; editable shares (permission `edit`) show a source editor + live
 * preview and can write back via `PUT /api/shares/:token` (the token is the
 * capability — no sign-in required to edit).
 */
export function SharePage() {
  useTheme();
  const t = useT();
  const { token: tokenParam, slug } = useParams<{ token: string; slug: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ status: 'loading' });
  // The canonical share token — the URL param for `/s/:token`, or resolved from
  // the slug for a published `/p/:slug` link (used for collab room + write-back).
  const [token, setToken] = useState<string | undefined>(tokenParam);
  const [draft, setDraft] = useState('');
  const [title, setTitle] = useState('');
  const [save, setSave] = useState<SaveState>('idle');
  const [peers, setPeers] = useState(1);
  const [presence, setPresence] = useState<CollabUser[]>([]);
  const bodyRef = useRef<HTMLDivElement>(null);
  const collabUser = useMemo(() => makeCollabUser(Math.floor(Math.random() * 1e6)), []);
  const autosaveTimer = useRef<number | null>(null);
  /** Accepted password for a protected share, sent on read + write requests. */
  const passwordRef = useRef<string>('');
  const [pwInput, setPwInput] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  /**
   * A deletion link (`…/s/<token>#delete=<secret>`) lets whoever made the share
   * take it back without an account. The secret stays in the fragment, so it
   * never reaches a server log.
   */
  const deleteSecret = useMemo(
    () => new URLSearchParams(window.location.hash.slice(1)).get('delete') ?? '',
    [],
  );
  const [deleteState, setDeleteState] = useState<'idle' | 'busy' | 'done' | 'failed'>('idle');

  const deleteShare = async () => {
    setDeleteState('busy');
    try {
      const res = await fetch(`/api/shares/${tokenParam}`, {
        method: 'DELETE',
        headers: { 'x-delete-secret': deleteSecret },
      });
      if (res.ok || res.status === 404) {
        setDeleteState('done');
        void forgetLocalShare(tokenParam ?? '');
        setState({ status: 'error', message: t('sp.deleted') });
      } else {
        setDeleteState('failed');
      }
    } catch {
      setDeleteState('failed');
    }
  };

  const load = async (pw?: string) => {
    try {
      const headers: Record<string, string> = {};
      if (pw) headers['x-share-password'] = pw;
      const url = slug ? `/api/shares/by-slug/${slug}` : `/api/shares/${tokenParam}`;
      const res = await fetch(url, { headers });
      // `/p/<slug>` is one address space for single notes and for sites; a
      // slug no note answers to may still be a site's.
      if (res.status === 404 && slug) return setState({ status: 'site' });
      if (res.status === 404) return setState({ status: 'error', message: t('sp.invalid') });
      if (res.status === 401) return setState({ status: 'password' });
      if (res.status === 403) return setState({ status: 'password', error: t('sp.wrongPassword') });
      if (!res.ok) throw new Error(`Request failed (${res.status}).`);
      const doc = (await res.json()) as GetShareResponse;
      if (doc.token) setToken(doc.token); // slug → canonical token
      passwordRef.current = pw ?? '';
      setState({ status: 'ready', doc });
      setDraft(doc.content);
      setTitle(doc.title);
    } catch (err) {
      setState({ status: 'error', message: err instanceof Error ? err.message : t('sp.invalid') });
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenParam, slug]);

  const editable = state.status === 'ready' && state.doc.permission === 'edit';
  const collab = editable && collabEnabled();
  const source = editable ? draft : state.status === 'ready' ? state.doc.content : '';
  const html = useMemo(() => render(source), [source]);

  /** Collaborative edits stream in through Yjs; mirror + debounce-autosave. */
  const onCollabText = (text: string) => {
    setDraft(text);
    if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
    autosaveTimer.current = window.setTimeout(() => {
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      if (passwordRef.current) headers['x-share-password'] = passwordRef.current;
      void fetch(`/api/shares/${token}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ title: title || 'Untitled', content: text }),
      }).catch(() => {});
    }, 1500);
  };

  useEffect(() => {
    if (bodyRef.current && html) {
      bodyRef.current.innerHTML = html;
      void renderMermaid(bodyRef.current);
      void renderKatex(bodyRef.current);
      void resolveImagesInDom(bodyRef.current);
    }
  }, [html]);

  const copyToLibrary = async () => {
    if (state.status !== 'ready') return;
    const doc = await createDocument({ title: title || state.doc.title, content: source });
    await setLastOpenDocId(doc.id);
    navigate('/', { replace: true });
  };

  const saveEdit = async () => {
    if (!editable) return;
    setSave('saving');
    try {
      const payload: UpdateShareRequest = { title: title || 'Untitled', content: draft };
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      if (passwordRef.current) headers['x-share-password'] = passwordRef.current;
      const res = await fetch(`/api/shares/${token}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status}).`);
      setSave('saved');
      setTimeout(() => setSave((s) => (s === 'saved' ? 'idle' : s)), 1800);
    } catch {
      setSave('error');
    }
  };

  // Below every hook, so the handover does not change how many run.
  if (state.status === 'site') return <SitePage />;

  return (
    <div className="mb-share-page">
      <header className="mb-share-header">
        <span className="mb-share-brand">markbird</span>
        <span className={`mb-share-badge ${editable ? 'mb-share-badge-edit' : ''}`}>
          {editable ? t('sp.editable') : t('sp.readonly')}
        </span>
        {collab && peers > 1 && (
          <span className="mb-share-peers" title={t('sp.peersTitle')}>
            <Icon name="dot" size={10} /> {t('sp.peers', { n: peers })}
          </span>
        )}
        {collab && presence.length > 0 && (
          <span className="mb-presence" aria-label={t('sp.peersTitle')}>
            {presence.slice(0, 6).map((u, i) => (
              <span
                key={`${u.name}-${i}`}
                className="mb-presence-dot"
                style={{ background: u.color }}
                title={u.name}
              >
                {u.name.slice(0, 1).toUpperCase()}
              </span>
            ))}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {editable && (
          <button
            className="mb-btn mb-btn-primary"
            disabled={
              save === 'saving' || draft === (state.status === 'ready' ? state.doc.content : '')
            }
            onClick={() => void saveEdit()}
          >
            {save === 'saving'
              ? t('sp.saving')
              : save === 'saved'
                ? t('sp.saved')
                : save === 'error'
                  ? t('sp.retry')
                  : t('sp.save')}
          </button>
        )}
        {state.status === 'ready' && (
          <button className="mb-btn" onClick={() => void copyToLibrary()}>
            {t('sp.copyLib')}
          </button>
        )}
        <a className="mb-btn" href="/">
          {t('sp.open')}
        </a>
      </header>

      {deleteSecret && deleteState !== 'done' && (
        <div className="mb-share-delete-bar" role="region" aria-label={t('sp.deleteTitle')}>
          <span>{deleteState === 'failed' ? t('sp.deleteFailed') : t('sp.deleteHint')}</span>
          <button
            className="mb-btn mb-btn-danger"
            disabled={deleteState === 'busy'}
            onClick={() => void deleteShare()}
          >
            {deleteState === 'busy' ? t('sp.deleting') : t('sp.deleteNow')}
          </button>
        </div>
      )}

      <main className={editable ? 'mb-share-main mb-share-main-edit' : 'mb-share-main'}>
        {state.status === 'loading' && <p className="mb-share-note">{t('sp.loading')}</p>}
        {state.status === 'password' && (
          <form
            className="mb-share-note mb-share-password"
            onSubmit={(e) => {
              e.preventDefault();
              if (pwInput.trim()) void load(pwInput.trim());
            }}
          >
            <h1>
              <Icon name="lock" size={20} /> {t('sp.protectedTitle')}
            </h1>
            <p>{t('sp.protectedHint')}</p>
            <input
              type="password"
              autoFocus
              autoComplete="current-password"
              className="mb-modal-url"
              placeholder={t('shareOpt.passwordPlaceholder')}
              value={pwInput}
              onChange={(e) => setPwInput(e.target.value)}
              aria-label={t('shareOpt.password')}
            />
            {state.error && <p className="mb-import-error">{state.error}</p>}
            <button className="mb-btn mb-btn-primary" type="submit" disabled={!pwInput.trim()}>
              {t('sp.unlock')}
            </button>
          </form>
        )}
        {state.status === 'error' && (
          <div className="mb-share-note">
            <h1>{t('sp.notAvailable')}</h1>
            <p>{state.message}</p>
            <a className="mb-btn mb-btn-primary" href="/">
              {t('sp.goto')}
            </a>
          </div>
        )}
        {editable && (
          <div className="mb-share-editor">
            <input
              className="mb-share-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('sp.titlePlaceholder')}
              aria-label={t('toolbar.docTitle')}
            />
            {collab && state.status === 'ready' ? (
              <CollabEditor
                token={token!}
                initialContent={state.doc.content}
                user={collabUser}
                onTextChange={onCollabText}
                onPeers={setPeers}
                onPresence={setPresence}
              />
            ) : (
              <textarea
                className="mb-share-textarea"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                spellCheck={false}
                aria-label="Markdown source"
              />
            )}
          </div>
        )}
        {state.status === 'ready' && (
          <article ref={bodyRef} className="markbird-preview markdown-preview" />
        )}
      </main>

      {/* Quiet, but on every shared page: whoever a document is about has to be
          able to find this without an account and without knowing it exists. */}
      {state.status === 'ready' && (
        <footer className="mb-share-footer">
          <button type="button" className="mb-share-report" onClick={() => setReportOpen(true)}>
            {t('report.open')}
          </button>
        </footer>
      )}
      {reportOpen && (
        <ReportDialog
          target={slug ? { slug } : { token: tokenParam ?? '' }}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  );
}
